import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api";
import ProductCard from "../components/ProductCard";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import SEO from "../components/SEO";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const { add, setOpen } = useCart();

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  // Handle ?reorder=slug1,slug2,... from receipt email — pre-fill cart with prior order
  useEffect(() => {
    const reorder = searchParams.get("reorder");
    if (!reorder || products.length === 0) return;
    const slugs = reorder.split(",").map((s) => s.trim()).filter(Boolean);
    let added = 0;
    slugs.forEach((slug) => {
      const p = products.find((x) => x.slug === slug);
      if (p && p.variants && p.variants[0]) {
        add(p, p.variants[0], 1);
        added++;
      }
    });
    if (added > 0) {
      toast.success(`${added} item${added > 1 ? "s" : ""} added to your cart from your last order.`);
      setOpen(true);
    }
    // Clear the query param so a refresh doesn't re-add items
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(products.map((p) => p.category)))],
    [products]
  );

  const filtered = useMemo(() => {
    const base = products.filter(
      (p) =>
        (filter === "All" || p.category === filter) &&
        (query === "" || p.name.toLowerCase().includes(query.toLowerCase()))
    );
    // Surgical adjacency: move each `{compound}-nasal-spray` product to
    // sit immediately after its matching vial `{compound}` in the grid so
    // shoppers see both delivery formats side by side. This is a pure
    // client-side reorder — no data model changes, no styling changes,
    // no impact on any other product. (Owner request 2026-02.)
    const NASAL_SIBLINGS = {
      selank: "selank-nasal-spray",
      semax: "semax-nasal-spray",
      "mots-c": "mots-c-nasal-spray",
      "nad-plus": "nad-plus-nasal-spray",
    };
    const bySlug = Object.fromEntries(base.map((p) => [p.slug, p]));
    const consumed = new Set();
    const ordered = [];
    for (const p of base) {
      if (consumed.has(p.slug)) continue;
      ordered.push(p);
      consumed.add(p.slug);
      const siblingSlug = NASAL_SIBLINGS[p.slug];
      if (siblingSlug && bySlug[siblingSlug] && !consumed.has(siblingSlug)) {
        ordered.push(bySlug[siblingSlug]);
        consumed.add(siblingSlug);
      }
    }
    return ordered;
  }, [products, filter, query]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <SEO
        title="Shop Research Peptides — Full Catalog"
        description="Browse the full ProSource Labs catalog of research-grade peptides. BPC-157, TB-500, Tirzepatide, Retatrutide, Semaglutide, NAD+, GHK-Cu, and more. Tiered volume pricing, lab-verified, US domestic shipping."
        path="/shop"
        keywords="buy research peptides, peptide catalog, BPC-157 for sale, Tirzepatide research, GLP-1 research peptides, peptides USA"
      />
      <div className="overline">CATALOG</div>
      <h1 className="font-heading text-4xl sm:text-5xl mt-3 font-light">
        Research Compounds
      </h1>
      <p className="text-sm text-[#8A94A6] mt-3 max-w-xl">
        {products.length} compounds available · Multiple sizes per SKU · Strictly for research use.
      </p>

      <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between border-y border-white/10 py-4">
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 text-[10px] font-mono-tech uppercase tracking-[0.2em] border transition-colors ${
                filter === c
                  ? "border-[#00C3FF] text-[#00C3FF]"
                  : "border-white/10 text-white/60 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <input
          data-testid="shop-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search compounds..."
          className="input-tech max-w-xs"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-white/5 mt-px">
        {filtered.map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center text-[#8A94A6] py-20 font-mono-tech text-sm">No compounds match your search.</div>
      )}
    </div>
  );
}
